import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import type { LoroDoc } from "loro-crdt";
import type { DocHandle } from "../sync/docManager";
import type { ViewCtx } from "../registry/viewTypes";
import { useNow } from "../core/useNow";
import type { Level } from "../core/schema";
import { useLevels } from "./levels";
import { useReadOnly } from "./useReadOnly";
import { useSession } from "./session";

/** The context every widget receives (spec §9.7). */
export function useViewCtx(handle: DocHandle | null, workspaceId: string): ViewCtx | null {
  const now = useNow();
  const navigate = useNavigate();
  const location = useLocation();
  const readOnly = useReadOnly(handle, workspaceId);
  const kind = (useSession((s) => s.session?.profile?.kind) ?? "person") as "person" | "kid" | "viewer";
  // the person's level (ADR 0019); the server already gives kids the reduced level (spec §9.29)
  const level: Level = useLevels().settings;
  // the panel opens beside whatever page is showing (spec §10: `/t/:id` deep links redirect here)
  const openTask = useCallback(
    (id: string) => {
      const params = new URLSearchParams(location.search);
      params.set("task", id);
      navigate({ pathname: location.pathname, search: `?${params.toString()}` });
    },
    [navigate, location.pathname, location.search],
  );
  const write = useCallback(
    (fn: (doc: LoroDoc) => void) => {
      if (!handle || handle.readOnly || readOnly) return;
      fn(handle.doc);
    },
    [handle, readOnly],
  );
  return useMemo(
    () =>
      handle
        ? { doc: handle.doc, handle, workspaceId, now, level, readOnly, profileKind: kind, openTask, write }
        : null,
    [handle, workspaceId, now, level, readOnly, kind, openTask, write],
  );
}
